import { useMemo } from 'react';
import type { MonthlyUpdate } from '../types';

/**
 * Lightweight zero-dep SVG line chart for the three 1–6 ratings on monthly
 * reports. X-axis: months since founding; Y-axis: 1–6. Lines break when a
 * value is missing so gaps stay visible.
 */

const RATING_EMOJI = ['😰', '😟', '😐', '🙂', '😄', '🤩'];

const SERIES = [
  { key: 'reportSentiment', label: 'Report sentiment', cssClass: 'monthlyChart-series--sentiment' },
  { key: 'exitProbability', label: 'Exit probability', cssClass: 'monthlyChart-series--exit' },
  { key: 'companyHealth', label: 'Company health', cssClass: 'monthlyChart-series--health' },
] as const;

type SeriesKey = (typeof SERIES)[number]['key'];

type Props = {
  reports: MonthlyUpdate[];
  /** Founding year of the company; required for the x-axis baseline. */
  foundingYear?: number;
};

type Point = { x: number; report: MonthlyUpdate };

function monthsSinceFounded(periodMonth: string | undefined, foundingYear: number | undefined): number | null {
  if (!periodMonth || foundingYear === undefined) return null;
  const d = new Date(periodMonth);
  if (Number.isNaN(d.getTime())) return null;
  return (d.getUTCFullYear() - foundingYear) * 12 + d.getUTCMonth();
}

function getRating(r: MonthlyUpdate, key: SeriesKey): number | undefined {
  const v = r[key];
  if (typeof v !== 'number' || !Number.isFinite(v)) return undefined;
  return Math.min(6, Math.max(1, v));
}

function tickStep(rangeMonths: number): number {
  if (rangeMonths <= 12) return 1;
  if (rangeMonths <= 36) return 3;
  if (rangeMonths <= 96) return 6;
  return 12;
}

export function hasMonthlyMetricsChartData(
  reports: MonthlyUpdate[],
  foundingYear?: number,
): boolean {
  if (foundingYear === undefined) return false;

  const points = reports
    .map((r) => {
      const x = monthsSinceFounded(r.periodMonth, foundingYear);
      return x === null ? null : { x, report: r };
    })
    .filter((p): p is Point => p !== null);

  return points.some((p) => SERIES.some((s) => getRating(p.report, s.key) !== undefined));
}

export function MonthlyMetricsChart({ reports, foundingYear }: Props) {
  const points = useMemo<Point[]>(() => {
    return reports
      .map((r) => {
        const x = monthsSinceFounded(r.periodMonth, foundingYear);
        return x === null ? null : { x, report: r };
      })
      .filter((p): p is Point => p !== null)
      .sort((a, b) => a.x - b.x);
  }, [reports, foundingYear]);

  const hasAnyRating = useMemo(
    () =>
      points.some((p) => SERIES.some((s) => getRating(p.report, s.key) !== undefined)),
    [points]
  );

  if (!hasAnyRating || points.length === 0) {
    return null;
  }

  const W = 880;
  const H = 360;
  const M = { top: 22, right: 20, bottom: 52, left: 36 };
  const innerW = W - M.left - M.right;
  const innerH = H - M.top - M.bottom;

  const minX = Math.max(0, Math.floor(points[0].x) - 1);
  const maxX = Math.max(minX + 1, Math.ceil(points[points.length - 1].x) + 1);
  const xRange = maxX - minX;

  const xScale = (x: number) => M.left + ((x - minX) / xRange) * innerW;
  const yScale = (y: number) => M.top + ((6 - y) / 5) * innerH;

  const step = tickStep(xRange);
  const xTicks: number[] = [];
  const start = Math.ceil(minX / step) * step;
  for (let t = start; t <= maxX; t += step) xTicks.push(t);

  function buildSegments(key: SeriesKey): Array<Array<{ x: number; y: number; id: string }>> {
    const segs: Array<Array<{ x: number; y: number; id: string }>> = [];
    let current: Array<{ x: number; y: number; id: string }> = [];
    for (const p of points) {
      const v = getRating(p.report, key);
      if (v !== undefined) {
        current.push({ x: p.x, y: v, id: p.report.id });
      } else if (current.length > 0) {
        segs.push(current);
        current = [];
      }
    }
    if (current.length > 0) segs.push(current);
    return segs;
  }

  return (
    <div className="monthlyChart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="monthlyChart-svg"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Monthly ratings over time"
      >
        {[1, 2, 3, 4, 5, 6].map((y) => (
          <g key={y}>
            <line
              x1={M.left}
              x2={M.left + innerW}
              y1={yScale(y)}
              y2={yScale(y)}
              className="monthlyChart-gridY"
            />
            <text
              x={M.left - 10}
              y={yScale(y)}
              textAnchor="end"
              dominantBaseline="middle"
              className="monthlyChart-yTick"
            >
              {y}
            </text>
          </g>
        ))}

        {xTicks.map((t) => (
          <g key={t}>
            <line
              x1={xScale(t)}
              x2={xScale(t)}
              y1={M.top + innerH}
              y2={M.top + innerH + 6}
              className="monthlyChart-tick"
            />
            <text
              x={xScale(t)}
              y={M.top + innerH + 22}
              textAnchor="middle"
              className="monthlyChart-axisLabel"
            >
              {t}
            </text>
          </g>
        ))}
        <text
          x={M.left + innerW / 2}
          y={H - 8}
          textAnchor="middle"
          className="monthlyChart-axisTitle"
        >
          Months since founded
        </text>

        <line
          x1={M.left}
          x2={M.left}
          y1={M.top}
          y2={M.top + innerH}
          className="monthlyChart-axis"
        />
        <line
          x1={M.left}
          x2={M.left + innerW}
          y1={M.top + innerH}
          y2={M.top + innerH}
          className="monthlyChart-axis"
        />

        {SERIES.map((s) => {
          const segs = buildSegments(s.key);
          return (
            <g key={s.key} className={s.cssClass}>
              {segs.map((seg, i) => {
                if (seg.length < 2) return null;
                const d = seg
                  .map((p, j) => `${j === 0 ? 'M' : 'L'} ${xScale(p.x).toFixed(1)} ${yScale(p.y).toFixed(1)}`)
                  .join(' ');
                return (
                  <path
                    key={`${s.key}-line-${i}`}
                    d={d}
                    fill="none"
                    className="monthlyChart-line"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                );
              })}
              {points.map((p) => {
                const v = getRating(p.report, s.key);
                if (v === undefined) return null;
                return (
                  <circle
                    key={`${s.key}-pt-${p.report.id}`}
                    cx={xScale(p.x)}
                    cy={yScale(v)}
                    r={3.75}
                    className="monthlyChart-dot"
                  >
                    <title>{`${s.label} · month ${p.x}: ${v} (${RATING_EMOJI[v - 1]})`}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}
      </svg>

      <ul className="monthlyChart-legend">
        {SERIES.map((s) => (
          <li key={s.key} className={s.cssClass}>
            <span className="monthlyChart-swatch" aria-hidden />
            <span>{s.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
